import { useMemo } from 'react';

import { useQueries } from '@tanstack/react-query';

import { costPerPointQueryKeys } from '~/hooks/apis/costPerPoint/costPerPointQueryKeys';

import {
  type CppInvoiceTotals,
  type CppInvoiceTransactionType,
  type CppPartner,
  getCppInvoiceTransactions,
} from '~/services/LuxLoyalty';

import {
  type PartnerTransactionRow,
  mergePartnerTransactions,
  resolveSharedCurrency,
  sumInvoiceTotals,
} from './allPartnersTransactions';
import { fetchAllInvoiceTransactions } from './fetchAllTransactions';
import { type MonthRange } from './monthRange';

interface Args {
  partners: Array<CppPartner>;
  range: MonthRange;
  transactionType: CppInvoiceTransactionType | '';
  enabled: boolean;
}

/**
 * Loads every partner's transactions for one month and merges them.
 *
 * The endpoint requires a partner id, so "all partners" is one walk per partner. Each walk is its
 * own query, so paging between Transactions and Invoice does not repeat the requests.
 */
export function useAllPartnersTransactions({ partners, range, transactionType, enabled }: Args) {
  const queries = useQueries({
    queries: partners.map((partner) => ({
      queryKey: [
        ...costPerPointQueryKeys.invoices(),
        'transactions-all',
        partner.id,
        range.start,
        range.end,
        transactionType,
      ] as const,
      queryFn: ({ signal }) =>
        fetchAllInvoiceTransactions(
          { partnerId: partner.id, ...range, ...(transactionType && { transactionType }) },
          { fetchPage: getCppInvoiceTransactions, signal },
        ),
      enabled,
      staleTime: 60_000,
      retry: false,
    })),
  });

  const dataStamp = queries.map((query) => `${query.dataUpdatedAt}:${query.status}`).join('|');

  const rows: Array<PartnerTransactionRow> = useMemo(
    () =>
      mergePartnerTransactions(
        partners.flatMap((partner, index) => {
          const transactions = queries[index]?.data?.transactions;
          return transactions
            ? [{ partnerName: partner.name, currency: queries[index]?.data?.currency ?? null, transactions }]
            : [];
        }),
      ),
    // `dataStamp` changes when any partner's pages arrive or fail. `queries` is a fresh array
    // every render, so depending on it would rebuild the merged ledger on unrelated updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [partners, dataStamp],
  );

  const loaded = queries.filter((query) => query.isSuccess || query.isError);
  const failedPartners = partners.filter((_partner, index) => queries[index]?.isError);
  const blockedPartnerNames = partners.flatMap((partner, index) => {
    const page = queries[index]?.data;
    return page && !page.invoiceable ? [partner.name] : [];
  });
  const truncated = queries.some((query) => query.data?.truncated);
  const successfulTotals = queries.flatMap((query) => (query.data ? [query.data.totals] : []));
  const totals: CppInvoiceTotals | null = successfulTotals.length > 0 ? sumInvoiceTotals(successfulTotals) : null;
  const { currency, mixed: mixedCurrency } = resolveSharedCurrency(
    queries.map((query) => query.data?.currency ?? null),
  );

  return {
    rows,
    totals,
    currency,
    mixedCurrency,
    failedPartners,
    blockedPartnerNames,
    truncated,
    isLoading: enabled && queries.some((query) => query.isLoading),
    isFetching: enabled && queries.some((query) => query.isFetching),
    isSettled: !enabled || loaded.length === partners.length,
    loadedCount: loaded.length,
    partnerCount: partners.length,
  };
}
